import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import { supabase } from '@/supabase-client'

export default function SquareAuthCallback({ user }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState('processing')
  const [message, setMessage] = useState('Connecting to Square...')
  const [error, setError] = useState(null)
  const hasCalledCallback = useRef(false)

  useEffect(() => {
    if (hasCalledCallback.current) return
    hasCalledCallback.current = true
    handleCallback()
  }, [])

  const handleCallback = async () => {
    const errorParam = searchParams.get('error')
    if (errorParam) {
      setStatus('error')
      setError(searchParams.get('error_description') || 'Authorization was denied')
      setMessage('Connection failed')
      return
    }

    const code = searchParams.get('code')
    const state = searchParams.get('state')

    if (!code) {
      setStatus('error')
      setError('No authorization code received')
      setMessage('Connection failed')
      return
    }

    try {
      const { data, error: fnError } = await supabase.functions.invoke('square-market-sync', {
        body: { action: 'callback', code, state }
      })

      if (fnError) throw fnError

      if (data?.success) {
        setStatus('success')
        setMessage(`Connected to ${data.merchant?.business_name || 'Square'}`)
      } else {
        throw new Error(data?.error || 'Unknown error')
      }
    } catch (err) {
      setStatus('error')
      setError(err.message)
      setMessage('Connection failed')
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            {status === 'processing' && <Loader2 className="h-5 w-5 animate-spin" />}
            {status === 'success' && <CheckCircle2 className="h-5 w-5 text-green-600" />}
            {status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
            {message}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'error' && (
            <p className="text-sm text-muted-foreground">{error}</p>
          )}
          {status !== 'processing' && (
            <Button onClick={() => navigate('/sales/markets')}>
              Go to Markets
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
